class ReviewsCarousel extends HTMLElement {
  constructor() {
    super();
    this.wrapper = this.querySelector('.reviews-carousel__wrapper');
    this.slides = this.querySelectorAll('.reviews-carousel__slide');
    this.prevButton = this.querySelector('.reviews-carousel__button--prev');
    this.nextButton = this.querySelector('.reviews-carousel__button--next');
    this.progressBar = this.querySelector('.reviews-carousel__progress-bar');
    this.currentIndex = 0;
    this.slidesPerView = this.getSlidesPerView();
    this.lastWindowWidth = window.innerWidth;
    
    // Touch variables
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchStartY = 0; // Track vertical position to distinguish between horizontal and vertical swipes
    this.minSwipeDistance = 50; // Minimum distance to register a swipe
    this.isDragging = false;
    this.dragStartPosition = 0;
    this.isHorizontalSwipe = false;
    
    // Set touch-action CSS to improve mobile experience
    this.style.touchAction = 'pan-y'; // Allow vertical scrolling but handle horizontal ourselves
    this.wrapper.style.touchAction = 'pan-y';
    
    if (this.slides.length > 0) {
      // Set default transition
      this.wrapper.style.transition = 'transform 0.3s ease';
      
      this.init();
      this.updateButtonVisibility();
      this.updateProgressBar();
      
      // Debounced resize handler
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const newWindowWidth = window.innerWidth;
          const breakpointChanged = 
            (this.lastWindowWidth < 750 && newWindowWidth >= 750) || 
            (this.lastWindowWidth >= 750 && newWindowWidth < 750) ||
            (this.lastWindowWidth < 990 && newWindowWidth >= 990) || 
            (this.lastWindowWidth >= 990 && newWindowWidth < 990);
          
          this.lastWindowWidth = newWindowWidth;
          this.slidesPerView = this.getSlidesPerView();
          
          // Reset to first slide if breakpoint changed
          if (breakpointChanged) {
            this.currentIndex = 0;
          }
          
          this.updateSlidePosition();
          this.updateButtonVisibility();
          this.updateProgressBar();
        }, 250);
      });
    }
  }
  
  getSlidesPerView() {
    const viewportWidth = window.innerWidth;
    if (viewportWidth < 750) return 1;
    if (viewportWidth < 990) return 3;
    return 4;
  }
  
  init() {
    this.prevButton.addEventListener('click', () => this.slide('prev'));
    this.nextButton.addEventListener('click', () => this.slide('next'));
    
    // Add touch event listeners for swipe
    this.initSwipeEvents();
    
    // Handle keyboard navigation
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        this.slide('prev');
      } else if (event.key === 'ArrowRight') {
        this.slide('next');
      }
    });
  }
  
  initSwipeEvents() {
    // Touch start event
    this.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
      this.touchStartY = e.changedTouches[0].screenY;
      this.handleDragStart();
    }, { passive: true });
    
    // Touch move event for real-time feedback - non-passive to allow preventDefault
    this.addEventListener('touchmove', (e) => {
      if (this.isDragging) {
        const currentX = e.changedTouches[0].screenX;
        const currentY = e.changedTouches[0].screenY;
        
        // Determine if this is primarily a horizontal swipe
        if (!this.isHorizontalSwipe) {
          const deltaX = Math.abs(currentX - this.touchStartX);
          const deltaY = Math.abs(currentY - this.touchStartY);
          
          // If horizontal movement is greater than vertical, and exceeds threshold, mark as horizontal swipe
          if (deltaX > deltaY && deltaX > 10) {
            this.isHorizontalSwipe = true;
          }
        }
        
        // If this is a horizontal swipe, prevent default to avoid page scrolling
        if (this.isHorizontalSwipe) {
          e.preventDefault();
        }
        
        this.handleDragMove(currentX, currentY);
      }
    }, { passive: false });
    
    // Touch end event
    this.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.touchEndY = e.changedTouches[0].screenY;
      this.handleDragEnd();
      this.isHorizontalSwipe = false; // Reset for next interaction
    }, { passive: true });
    
    // Mouse events for desktop swipe simulation
    this.addEventListener('mousedown', (e) => {
      this.touchStartX = e.screenX;
      this.touchStartY = e.screenY;
      this.handleDragStart();
    });
    
    this.addEventListener('mousemove', (e) => {
      if (this.isDragging) {
        e.preventDefault(); // Prevent text selection during drag
        const currentX = e.screenX;
        const currentY = e.screenY;
        this.handleDragMove(currentX, currentY);
      }
    });
    
    this.addEventListener('mouseup', (e) => {
      if (!this.isDragging) return;
      this.touchEndX = e.screenX;
      this.touchEndY = e.screenY;
      this.handleDragEnd();
    });
    
    this.addEventListener('mouseleave', (e) => {
      if (!this.isDragging) return;
      this.touchEndX = e.screenX;
      this.touchEndY = e.screenY;
      this.handleDragEnd();
    });
  }
  
  handleDragStart() {
    this.isDragging = true;
    this.dragStartPosition = this.getCurrentPosition();
    this.style.cursor = 'grabbing';
    
    // Disable transition during drag for responsive feel
    this.wrapper.style.transition = 'none';
  }
  
  handleDragMove(currentX, currentY) {
    if (!this.isDragging) return;
    
    const deltaX = currentX - this.touchStartX;
    const deltaY = currentY - this.touchStartY;
    
    // Only move horizontally if it's determined to be a horizontal swipe
    if (this.isHorizontalSwipe || Math.abs(deltaX) > Math.abs(deltaY)) {
      const slideElement = this.slides[0];
      const slideWidth = slideElement.offsetWidth;
      
      // Dampen movement at the ends (resistance effect)
      let moveX = this.dragStartPosition + deltaX;
      
      // Apply resistance at the beginning and end
      if (moveX > 0) {
        moveX = moveX * 0.3; // Resistance when trying to drag past the first slide
      } else if (Math.abs(moveX) > this.getMaxOffset()) {
        const overScroll = Math.abs(moveX) - this.getMaxOffset();
        moveX = -this.getMaxOffset() - overScroll * 0.3; // Resistance when trying to drag past the last slide
      }
      
      // Apply the movement
      this.wrapper.style.transform = `translateX(${moveX}px)`;
    }
  }
  
  handleDragEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.style.cursor = '';
    
    // Restore transition
    this.wrapper.style.transition = 'transform 0.3s ease';
    
    // Only process as a swipe if this was determined to be a horizontal swipe
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    if (this.isHorizontalSwipe && Math.abs(swipeDistance) >= this.minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swipe right - go to previous slide
        this.slide('prev');
      } else {
        // Swipe left - go to next slide
        this.slide('next');
      }
    } else {
      // Not enough distance was swiped, snap back to current position
      this.updateSlidePosition();
    }
    
    // Reset the horizontal swipe flag for the next interaction
    this.isHorizontalSwipe = false;
    
    // Explicitly remove any inline styles that might have been added during the operation
    setTimeout(() => {
      if (this.wrapper && !this.isDragging) {
        this.wrapper.style.removeProperty('will-change');
      }
    }, 350); // A bit longer than the transition time
  }
  
  getCurrentPosition() {
    // Extract current translateX value - cross-browser compatible
    const style = window.getComputedStyle(this.wrapper);
    const transform = style.transform || style.webkitTransform || style.mozTransform;
    
    if (transform === 'none' || !transform) {
      return 0;
    }
    
    let matrix;
    try {
      // Try the standard way first
      if (window.DOMMatrix) {
        matrix = new DOMMatrix(transform);
        return matrix.m41;
      } 
      // Fallback for older browsers
      else if (window.WebKitCSSMatrix) {
        matrix = new WebKitCSSMatrix(transform);
        return matrix.m41;
      } 
      // Manual parsing fallback
      else {
        const matrixValues = transform.match(/matrix.*\((.+)\)/)[1].split(', ');
        return parseFloat(matrixValues[4]);
      }
    } catch (e) {
      // If all else fails, parse manually
      const matrixMatch = transform.match(/matrix\(([^)]+)\)/);
      if (matrixMatch && matrixMatch[1]) {
        const matrixValues = matrixMatch[1].split(',');
        if (matrixValues.length >= 5) {
          return parseFloat(matrixValues[4]);
        }
      }
      return 0;
    }
  }
  
  getMaxOffset() {
    const slideElement = this.slides[0];
    const slideStyles = window.getComputedStyle(slideElement);
    const slideWidth = slideElement.offsetWidth;
    
    let gap = parseInt(slideStyles.marginRight, 10) || 0;
    if (isNaN(gap) || gap === 0) {
      gap = window.innerWidth < 750 ? 16 : 24;
    }
    
    const wrapperWidth = this.wrapper.offsetWidth;
    const totalSlidesWidth = this.slides.length * (slideWidth + gap) - gap;
    return Math.max(0, totalSlidesWidth - wrapperWidth);
  }
  
  updateButtonVisibility() {
    const maxIndex = Math.max(0, this.slides.length - this.slidesPerView);
    this.prevButton.disabled = this.currentIndex <= 0;
    this.nextButton.disabled = this.currentIndex >= maxIndex;
  }
  
  updateProgressBar() {
    const maxIndex = Math.max(0, this.slides.length - this.slidesPerView);
    const progress = (this.currentIndex / maxIndex) * 100;
    this.progressBar.style.width = `${Math.min(100, progress)}%`;
  }
  
  slide(direction) {
    const maxIndex = Math.max(0, this.slides.length - this.slidesPerView);
    const step = 1; // Move by one slide at a time
    
    if (direction === 'prev' && this.currentIndex > 0) {
      this.currentIndex = Math.max(0, this.currentIndex - step);
    } else if (direction === 'next' && this.currentIndex < maxIndex) {
      this.currentIndex = Math.min(maxIndex, this.currentIndex + step);
    }
    
    this.updateSlidePosition();
    this.updateButtonVisibility();
    this.updateProgressBar();
  }
  
  updateSlidePosition() {
    // Calculate the exact width including any gap/margin
    const slideElement = this.slides[0];
    const slideStyles = window.getComputedStyle(slideElement);
    const slideWidth = slideElement.offsetWidth;
    
    // Use actual gap from computed styles if possible, otherwise fall back to responsive value
    let gap = parseInt(slideStyles.marginRight, 10) || 0;
    if (isNaN(gap) || gap === 0) {
      gap = window.innerWidth < 750 ? 16 : 24;
    }
    
    // Ensure we're not moving too far when on mobile
    let offset = this.currentIndex * (slideWidth + gap);
    
    // Constrain maximum offset to prevent going beyond the last slide
    const wrapperWidth = this.wrapper.offsetWidth;
    const totalSlidesWidth = this.slides.length * (slideWidth + gap) - gap;
    const maxOffset = Math.max(0, totalSlidesWidth - wrapperWidth);
    offset = Math.min(offset, maxOffset);
    
    this.wrapper.style.transform = `translateX(-${offset}px)`;
  }
}

customElements.define('reviews-carousel', ReviewsCarousel); 